import type { AgentSessionDto, AgentMessageDto } from './session';

/**
 * Agent function request status values.
 */
export const AgentFunctionRequestStatus = {
  Pending: 0,
  Processing: 1,
  Completed: 2,
  Failed: 3,
} as const;

export type AgentFunctionRequestStatus =
  (typeof AgentFunctionRequestStatus)[keyof typeof AgentFunctionRequestStatus];

/**
 * Agent function list DTO.
 */
export interface AgentFunctionDto {
  id: string;
  name: string;
  slug: string;
  description?: string;
  isActive: boolean;
  agentDefinitionId: string;
  agentDefinitionName: string;
  version: string;
  requestCount: number;
  createdAt: string;
  tenantId: string;
  projectId?: string;
}

/**
 * Agent function detail DTO.
 */
export interface AgentFunctionDetailDto {
  id: string;
  name: string;
  slug: string;
  description?: string;
  inputSchemaJson: string;
  outputSchemaJson: string;
  systemPrompt: string;
  isActive: boolean;
  agentDefinitionId: string;
  agentDefinitionName: string;
  version: string;
  metadataJson?: string;
  requestCount: number;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  tenantId: string;
  projectId?: string;
}

/**
 * Agent function request DTO (execution history item).
 */
export interface AgentFunctionRequestDto {
  id: string;
  agentFunctionId: string;
  agentFunctionName: string;
  agentFunctionSlug: string;
  inputJson: string;
  outputJson?: string;
  status: AgentFunctionRequestStatus;
  sessionId?: string;
  fileId?: string;
  errorMessage?: string;
  startedAt?: string;
  completedAt?: string;
  durationMs?: number;
  createdAt: string;
  createdBy?: string;
}

/**
 * Agent function request detail DTO (with session data).
 */
export interface AgentFunctionRequestDetailDto extends AgentFunctionRequestDto {
  session?: AgentSessionDto;
  messages: AgentMessageDto[];
  files: AgentSessionFileDto[];
}

/**
 * Agent session file DTO (simplified for embedding).
 */
export interface AgentSessionFileDto {
  id: string;
  tenantId: string;
  projectId?: string;
  sessionId: string;
  fileName: string;
  originalFileName: string;
  contentType: string;
  fileSizeBytes: number;
  category: number;
  version: number;
  isLatestVersion: boolean;
  description?: string;
  metadataJson?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Request model for creating an agent function.
 */
export interface CreateAgentFunctionRequest {
  name: string;
  slug?: string;
  description?: string;
  inputSchemaJson: string;
  outputSchemaJson: string;
  systemPrompt: string;
  isActive?: boolean;
  agentDefinitionId: string;
  metadataJson?: string;
}

/**
 * Request model for updating an agent function.
 */
export interface UpdateAgentFunctionRequest {
  name: string;
  description?: string;
  inputSchemaJson: string;
  outputSchemaJson: string;
  systemPrompt: string;
  isActive: boolean;
  agentDefinitionId: string;
  metadataJson?: string;
}

/**
 * Filters for agent function queries.
 */
export interface AgentFunctionFilters {
  pageNumber?: number;
  pageSize?: number;
  isActive?: boolean;
  searchTerm?: string;
}

/**
 * Filters for agent function request queries.
 */
export interface AgentFunctionRequestFilters {
  pageNumber?: number;
  pageSize?: number;
  status?: AgentFunctionRequestStatus;
}
